import React, {useMemo, useState} from 'react';
// import Layout from './Layout';
import {eye, lookupEmptySvg} from 'assets/images/images';
import {
  EmptyState,
  Filter,
  FilterButton,
  Loader,
  PageLimit,
  Pagination,
  SearchInput,
  TableLayout,
} from 'components';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useQuery} from 'react-query';
import {fetchAccountTransactions} from '../../../../../requests/queries/flows';
import AppliedFilters from '../../../../../components/AppliedFilters';
import {filterOptions} from '../../utils';
import {filterOutEmptyValues} from '../../../../../helpers';
import moment from 'moment/moment';
import {shortenLargeNumbers} from '../../../../../helpers/shortenLargeNumbers';
import {getStatusTextAndStyles} from '../../../../../helpers/getStatusTextAndStyles';
import DashboardLayout from '../../../../../components/DashboardLayout';
import Breadcrumbs from '../../../../../components/BreadCrumbs';
import {PAGES_URL} from '../../../../../helpers/constants';

function SingleAccountEvents() {
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [filterProps, setFilterProps] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    start: null,
    end: null,
  });
  const [currentPage, setCurrentPage] = useState('1');
  const [limit, setLimit] = useState(10);
  const {state} = useLocation();
  const {id} = useParams();

  const getSlugFromPath = () => {
    const pathParts = location.pathname.split('/');
    return pathParts[3];
  };

  const handleRowClick = event => {
    const slug = getSlugFromPath();
    navigate(`/flows/event/${slug}/details/${event.id}`);
  };

  const params = useMemo(
    () => ({
      account_id: state?.profile_id || id,
      params: {
        ...filters,
        limit,
        page: currentPage,
      },
    }),
    [state?.profile_id, id, filters, limit, currentPage],
  );

  const {data: accountEvents, isLoading} = useQuery(
    ['users-accounts-transactions', params],
    () => fetchAccountTransactions(params),
  );

  const pageClick = selected => {
    setCurrentPage(selected);
  };

  const EVENTS = useMemo(
    () => (!isLoading && accountEvents && accountEvents?.rows) || [],
    [accountEvents, isLoading],
  );

  const handleFilter = selectedOptions => {
    const filterParams = filterOutEmptyValues(selectedOptions);
    const params = Object.fromEntries(
      Object.entries(filterParams).map(([key, value]) => [
        key,
        Array.isArray(value) ? value?.join(',') : value,
      ]),
    );

    setFilters({
      ...params,
    });

    setOpenFilter(false);
  };

  const handleResetFilter = () => {
    setFilters({
      start: '',
      end: '',
    });

    setOpenFilter(false);
  };

  const handleFilterStateChange = props => {
    setFilterProps(props);
    setFiltersApplied(!!props);
  };

  return (
    // <Layout pageTitle="Events" pageIcon={profileIc}>
    <DashboardLayout
      bg="bg-white"
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Profile Details</p>
      }
    >
      <div className="flex bg-white items-center min-h-[52px] py-2 flex-wrap gap-3 justify-between mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E3E6]">
        <Breadcrumbs
          items={[
            {
              label: 'Profile Details',
              path: PAGES_URL.PROFILED_RISK_PROFILE_DETAILS.replace(
                ':slug',
                id,
              ),
              state: {...state},
            },
            {label: id},
          ]}
        />
      </div>
      <Filter
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        handleFilter={options => handleFilter(options, 'filter')}
        resetUrl={handleResetFilter}
        filterOptions={filterOptions}
        onFilterStateChange={handleFilterStateChange}
      />

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-wrap mt-7">
            <h2 className="text-base font-medium text-grey sm:text-lg">
              Events
            </h2>
            {filtersApplied && filterProps && (
              <AppliedFilters {...filterProps} isMainPage={true} />
            )}
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <SearchInput
              onChange={value => {
                setFilters(prev => ({...prev, search: value, page: 1}));
              }}
            />
            <FilterButton
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </div>
        </div>

        <TableLayout negativeMargins className="mt-6">
          <thead className="text-xs font-semibold uppercase">
            <tr className="bg-white80">
              <th className="p-5 pl-12 font-medium text-left text-xs text-grey whitespace-nowrap w-[22%]">
                Timestamp
              </th>
              <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap w-[20%]">
                Event ID
              </th>
              <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap w-[15%]">
                Event Type
              </th>
              <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap w-[15%]">
                Amount
              </th>
              <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap w-[15%]">
                Status
              </th>
              <th className="p-5 pl-0 font-medium text-left text-xs text-grey whitespace-nowrap w-[15%]"></th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td colSpan={8}>
                {isLoading ? (
                  <Loader height={45} />
                ) : !isLoading && EVENTS?.length === 0 ? (
                  <EmptyState
                    body="No event has been recorded yet. When there is, it will come up here"
                    noBtn
                    height={55}
                    src={lookupEmptySvg}
                  />
                ) : null}
              </td>
            </tr>
          </tbody>

          <tbody className="text-sm bg-white">
            {EVENTS &&
              EVENTS.map(event => (
                <tr
                  key={event?.id}
                  onClick={() => handleRowClick(event)}
                  className="border-b border-grey60 hover:bg-gray-50 cursor-pointer"
                >
                  <td className="p-4 capitalize pl-12 text-sm text-body whitespace-nowrap w-[22%]">
                    {moment(event?.createdAt).format('Do MMM YYYY hh:mm') ||
                      '-'}
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap w-[20%] uppercase">
                    {event?.transaction_id || ''}
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap w-[15%]">
                    <span className="uppercase text-white bg-[#4A878D] py-1 px-2 text-[10px] rounded-full">
                      {event.transaction_type?.replaceAll('_', ' ') || '-'}
                    </span>
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap w-[15%]">
                    {shortenLargeNumbers(Number(event?.amount), 'NGN', {
                      decimals: 1,
                      startAt: 'B',
                    })}
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap w-[15%]">
                    <span
                      className={`uppercase py-1 px-2 text-[10px] rounded-full ${
                        getStatusTextAndStyles(event?.final_outcome).styles
                      }`}
                    >
                      {getStatusTextAndStyles(event?.final_outcome).text || '-'}
                    </span>
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap w-[5%]">
                    <img src={eye} alt="View" />
                  </td>
                </tr>
              ))}
          </tbody>
        </TableLayout>

        {!isLoading && EVENTS?.length !== 0 && (
          <div className="flex items-center justify-between flex-wrap gap-4 sm:gap-0 mt-8">
            <div className="flex items-center text-body text-sm">
              <PageLimit
                onLimitChange={setLimit}
                total={accountEvents && accountEvents?.count}
                length={EVENTS?.length}
                limit={limit}
              />
            </div>

            <div className="mb-8 sm:mb-0">
              <Pagination
                total={accountEvents && Math.ceil(accountEvents?.count / limit)}
                current={+currentPage}
                onPageChange={activePage => {
                  localStorage.setItem('page', String(activePage));
                  pageClick(activePage);
                }}
              />
            </div>
          </div>
        )}
      </div>
      {/*</Layout>*/}
    </DashboardLayout>
  );
}

export default SingleAccountEvents;
